/**
 * One conversational turn, end to end: send, stream, speak, settle.
 *
 * The whole product is this loop, so the rules it enforces are worth stating:
 *
 * 1. The transcript holds messages the server has confirmed, plus at most ONE
 *    unconfirmed row: the sentence just sent, from the moment it is typed until
 *    `user_message` swaps the stored row in. A turn that fails keeps that row —
 *    their words are not deleted because a request did not land — and the next
 *    attempt overwrites it rather than appending beside it, so the count on screen
 *    can never drift from the count a reload returns by more than that one row.
 *    Partial REPLY text lives in `streamingText` until `done` carries the stored
 *    row, which is why an aborted turn can drop its tail without leaving behind a
 *    message that a reload would not bring back.
 * 2. Voice is peripheral. Every failure on the speech path degrades to text in
 *    silence; none of them reaches `error`.
 * 3. Results from a turn the user has already moved past are discarded rather than
 *    applied, so cutting in cannot resurrect the previous reply.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Finding, Locale, Message, Suggestion, TurnEvent } from "../../../shared/types";
import type { Respondent, TurnBody } from "./api";
import { ApiError, speak, speakStreamUrl, streamTurn } from "./api";
import type { Copy } from "./copy";
import { useCopy } from "./copy";

/** The blob's state machine. Mirrors the union exported by web/src/blob. */
export type BlobState = "idle" | "listening" | "thinking" | "speaking";

/**
 * Where a turn goes, so this loop can be reused by a caller talking to a different
 * set of endpoints.
 *
 * Wave 4 has two: the owner's `/api/conversations/:id/turn` and the respondent's
 * `/api/i/turn`, which is cookie-scoped to one participant and carries no
 * conversation id in its URL at all. The rules this hook enforces — the optimistic
 * row, one stranded retry, playback starting at `done`, results from a superseded
 * turn discarded — are the product, and a second copy of them for the respondent
 * bundle would be a second place for them to drift.
 *
 * Everything is optional and defaults to the owner's endpoints, so no existing
 * caller changes.
 */
export interface TurnTransport {
  stream(
    conversationId: number,
    body: TurnBody,
    onEvent: (event: TurnEvent) => void,
    signal?: AbortSignal,
  ): Promise<void>;
  /** Null when the URL would be too long to hand to an <audio> element. */
  speakStreamUrl(text: string, language: Locale, conversationId: number | null): string | null;
  speak(text: string, language: Locale, signal?: AbortSignal): Promise<string>;
}

const DEFAULT_TRANSPORT: TurnTransport = { stream: streamTurn, speakStreamUrl, speak };

export interface UseTurnOptions {
  conversationId: number | null;
  /** Defaults to the owner's endpoints. See `TurnTransport`. */
  transport?: TurnTransport;
  /** Drives both the copy and the voice the reply is spoken in. */
  language: Locale;
  initialMessages?: Message[];
  initialFindings?: Finding[];
  /** False when /api/health has already said this deploy has no voice path. */
  voice?: boolean;
  /**
   * Plays the reply. Resolves when playback ends or is stopped; the hook holds the
   * `speaking` state for exactly as long as this takes. May REJECT when playback
   * failed outright — that is what lets the streaming GET src fall back to the
   * buffered POST path instead of ending the reply in silence.
   */
  onSpeech?: (url: string, messageId: number) => void | Promise<void>;
  /** Fires when voice turns out to be missing, for the degraded status notice. */
  onVoiceUnavailable?: () => void;
  /**
   * What the founder could say next, offered once the reply has landed. Handed out
   * rather than held here: the page also fetches these on arrival and on reload, so
   * one owner of that state is simpler than two writers of it. Never fires with an
   * empty list — the server sends nothing when it has nothing.
   */
  onSuggestions?: (suggestions: Suggestion[]) => void;
  /** The model names the conversation after the second turn. */
  onTitle?: (title: string) => void;
  /**
   * The intake has learned who it is talking to: the server has written the
   * respondent, flipped this conversation to a session, and the same conversation
   * simply continues. The page updates its idea of the case; nothing navigates.
   */
  onIntakeComplete?: (respondent: Respondent) => void;
}

export interface Turn {
  state: BlobState;
  /** The reply as it arrives. Empty except between `thinking` and `done`. */
  streamingText: string;
  messages: Message[];
  findings: Finding[];
  /** Already in the product voice, ready to drop into the status line. */
  error: string | null;
  send: (text: string, inputMode: "text" | "voice") => Promise<void>;
  cancel: () => void;
  /**
   * The mic layer owns `listening`; nothing inside a turn can produce it. Applied
   * only at rest, so it can never overwrite `thinking` or `speaking`.
   */
  setListening: (active: boolean) => void;
  clearError: () => void;
  /** Replaces the transcript wholesale, for switching conversations. */
  reset: (messages: Message[], findings?: Finding[]) => void;
  voiceAvailable: boolean;
}

/**
 * Collected by the event callback and read after the stream closes. A plain object
 * rather than locals: TypeScript keeps narrowing a `let` that only a closure writes
 * to, and would have `reply` typed `null` at the point we check it.
 */
interface StreamResult {
  reply: Message | null;
  /** Set when the server synthesised the audio itself, so there is nothing to ask for. */
  speechUrl: string | null;
  failed: boolean;
  /**
   * Playback, started the moment `done` lands rather than after the stream closes.
   * The stream stays open past `done` to deliver `findings`, and extraction is a
   * second model call; waiting for it put several seconds of silence between the
   * reply appearing and the reply being spoken.
   */
  playback: Promise<void> | null;
}

export function useTurn(options: UseTurnOptions): Turn {
  const { conversationId, language } = options;
  const copy = useCopy(language);

  const [state, setState] = useState<BlobState>("idle");
  const [streamingText, setStreamingText] = useState("");
  const [messages, setMessages] = useState<Message[]>(() => options.initialMessages ?? []);
  const [findings, setFindings] = useState<Finding[]>(() => options.initialFindings ?? []);
  const [error, setError] = useState<string | null>(null);
  const [voiceAvailable, setVoiceAvailable] = useState(options.voice !== false);

  // Options are read through a ref so a parent that re-creates its callbacks inline
  // on every render does not invalidate `send` in the middle of a conversation.
  const latest = useRef(options);
  latest.current = options;

  const abortRef = useRef<AbortController | null>(null);
  // Monotonic, so anything that resolves late can ask whether it is still wanted.
  const turnRef = useRef(0);
  const mountedRef = useRef(true);
  /**
   * The optimistic row of a turn that failed, still on screen and still unconfirmed
   * by any server row.
   *
   * Their words do not vanish because a request did not land — that is what the dock
   * hands back and what the transcript keeps. But it is ONE sentence, so the next
   * attempt overwrites this row instead of appending beside it: three presses of
   * Retry used to leave three copies of the same sentence (29 -> 32 turns) and a
   * reload deleted all three at once.
   */
  const strandedRef = useRef<number | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const isCurrent = useCallback(
    (id: number) => mountedRef.current && turnRef.current === id,
    [],
  );

  /** Retires the turn in flight: nothing it produces from here on is applied. */
  const endTurn = useCallback(() => {
    turnRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    endTurn();
    // The tail goes rather than staying: it was never confirmed as a stored message,
    // and keeping it would put words in the transcript that no reload would return.
    setStreamingText("");
    setState("idle");
  }, [endTurn]);

  const setListening = useCallback((active: boolean) => {
    setState((prev) => {
      if (active) return prev === "idle" ? "listening" : prev;
      return prev === "listening" ? "idle" : prev;
    });
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback(
    (next: Message[], nextFindings?: Finding[]) => {
      endTurn();
      strandedRef.current = null;
      setMessages(next);
      if (nextFindings !== undefined) setFindings(nextFindings);
      setStreamingText("");
      setError(null);
      setState("idle");
    },
    [endTurn],
  );

  /**
   * Fetches and plays the spoken reply. Every path through here ends at `idle` with
   * `error` untouched: a conversation that reads correctly but does not speak is a
   * working conversation, and saying otherwise on screen is worse than the silence.
   */
  const playReply = useCallback(
    async (
      message: Message,
      serverUrl: string | null,
      myTurn: number,
      signal: AbortSignal,
    ): Promise<void> => {
      const settle = (): void => {
        if (isCurrent(myTurn)) setState("idle");
      };

      const onSpeech = latest.current.onSpeech;
      if (!onSpeech || latest.current.voice === false || message.content.trim().length === 0) {
        settle();
        return;
      }

      let url = serverUrl;
      // Only a URL we minted is ours to revoke; one the server handed us belongs to
      // the server and outlives this turn.
      let owned = false;

      if (url === null) {
        // The streaming path first: the GET endpoint is handed straight to the
        // <audio> element, which starts playing on the first chunks — about half a
        // second in — instead of after the whole file has been synthesised and
        // downloaded. Anything wrong with it (a deploy without the route, a proxy
        // that buffers, a 431 on a long URL) rejects, and the buffered POST below
        // is the same fallback it always was.
        const streamUrl = (latest.current.transport ?? DEFAULT_TRANSPORT).speakStreamUrl(
          message.content,
          latest.current.language,
          latest.current.conversationId,
        );
        if (streamUrl !== null) {
          setState("speaking");
          try {
            await onSpeech(streamUrl, message.id);
            settle();
            return;
          } catch (err) {
            if (!isCurrent(myTurn)) return;
            console.warn("[turn] streaming speech failed, trying the buffered path:", err);
          }
        }
        try {
          url = await (latest.current.transport ?? DEFAULT_TRANSPORT).speak(
            message.content,
            latest.current.language,
            signal,
          );
          owned = true;
        } catch (err) {
          if (isAbort(err) || !isCurrent(myTurn)) return;
          if (err instanceof ApiError && err.isVoiceUnavailable) {
            setVoiceAvailable(false);
            latest.current.onVoiceUnavailable?.();
          } else {
            console.warn("[turn] speech failed, continuing in text:", err);
          }
          settle();
          return;
        }
      }

      if (!isCurrent(myTurn)) {
        if (owned) URL.revokeObjectURL(url);
        return;
      }

      setState("speaking");
      try {
        await onSpeech(url, message.id);
      } catch (err) {
        // An autoplay refusal lands here. The reply is already on screen, so it is
        // not something to interrupt the user about.
        console.warn("[turn] playback failed, continuing in text:", err);
      } finally {
        if (owned) URL.revokeObjectURL(url);
        settle();
      }
    },
    [isCurrent],
  );

  const send = useCallback(
    async (text: string, inputMode: "text" | "voice"): Promise<void> => {
      const body = text.trim();
      if (body.length === 0) return;
      if (conversationId === null) {
        setError(copy.errorGeneric);
        return;
      }

      // Sending during a turn is cutting in, not queueing: the previous reply stops
      // and its tail is discarded.
      endTurn();
      turnRef.current += 1;
      const myTurn = turnRef.current;
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      setStreamingText("");
      setState("thinking");

      // Put their words on screen NOW, before the request goes out. The server
      // echoes the stored row back as `user_message` a beat later and the negative
      // id is swapped for the real one, but that beat is the whole problem: it is
      // the moment the input has just emptied and nothing has replaced it, and it
      // is what made the page read as frozen.
      const provisionalId = -myTurn;
      const optimistic: Message = {
        id: provisionalId,
        role: "user",
        content: body,
        inputMode,
        createdAt: new Date().toISOString(),
      };
      // A retry (or anything sent after a turn that failed) REPLACES the row that
      // failed rather than appending under it. The server does the same thing on its
      // side — the turn endpoint reuses the unanswered row — so one row here is one
      // row there, and a reload can no longer delete something the founder can see.
      const stranded = strandedRef.current;
      strandedRef.current = null;
      setMessages((prev) => {
        const at = stranded === null ? -1 : prev.findIndex((m) => m.id === stranded);
        if (at === -1) return [...prev, optimistic];
        const next = prev.slice();
        next[at] = optimistic;
        return next;
      });

      const result: StreamResult = { reply: null, speechUrl: null, failed: false, playback: null };

      const onEvent = (event: TurnEvent): void => {
        if (!isCurrent(myTurn)) return;
        // Detected structurally rather than through the union: the wire can carry
        // it before this build's shared types do, and an unknown event must never
        // become an error mid-conversation either way.
        if ((event as { type: string }).type === "intake_complete") {
          const respondent = (event as unknown as { respondent?: Respondent }).respondent;
          if (respondent !== undefined && respondent !== null) {
            latest.current.onIntakeComplete?.(respondent);
          }
          return;
        }
        switch (event.type) {
          case "thinking":
            setState("thinking");
            break;
          case "user_message":
            // Replace the provisional row rather than appending beside it, or the
            // founder sees their own sentence twice. Confirmed, so nothing is
            // stranded any more even if the reply itself goes on to fail.
            strandedRef.current = null;
            setMessages((prev) => reconcile(prev, provisionalId, event.message));
            break;
          case "delta":
            setStreamingText((prev) => prev + event.text);
            break;
          case "speech":
            result.speechUrl = event.url;
            break;
          case "done":
            result.reply = event.message;
            setMessages((prev) => append(prev, event.message));
            setStreamingText("");
            if (event.title !== null) latest.current.onTitle?.(event.title);
            // Start speaking HERE, not after the stream closes. `findings` is still
            // to come and extraction is a second model call — waiting for the stream
            // to end meant the founder sat through several seconds of silence for a
            // background task they never see. Measured at 13.5s from send to first
            // audio; this is the difference between that and about five.
            result.playback = playReply(
              event.message,
              result.speechUrl,
              myTurn,
              controller.signal,
            );
            break;
          case "findings":
            setFindings(event.findings);
            break;
          case "suggestions":
            // Arrives after `done`, alongside `findings` and in no fixed order
            // against it. `isCurrent` above has already discarded anything from a
            // turn the founder has moved past, so these always belong to the reply
            // now on screen.
            latest.current.onSuggestions?.(event.suggestions);
            break;
          case "error":
            // The server writes this string in the product voice, so it is shown as
            // it arrives. Extraction may still be running behind it, so the stream
            // is left to close on its own rather than aborted here.
            result.failed = true;
            strandedRef.current = provisionalId;
            setError(event.message);
            setStreamingText("");
            setState("idle");
            break;
        }
      };

      try {
        await (latest.current.transport ?? DEFAULT_TRANSPORT).stream(
          conversationId,
          { text: body, inputMode },
          onEvent,
          controller.signal,
        );
      } catch (err) {
        if (isAbort(err) || !isCurrent(myTurn)) return;
        strandedRef.current = provisionalId;
        setError(describe(err, copy));
        setStreamingText("");
        setState("idle");
        return;
      }

      if (!isCurrent(myTurn) || result.failed) {
        // Playback started at `done` and is not cancelled by a later error event —
        // the reply was real and is already on screen, so let it finish speaking.
        await result.playback;
        return;
      }

      // A stream that closed without `done` is a truncated turn. Its partial text is
      // dropped for the same reason an abort drops it.
      if (result.reply === null) {
        strandedRef.current = provisionalId;
        setStreamingText("");
        setState("idle");
        setError(copy.errorGeneric);
        return;
      }

      await result.playback;
    },
    [conversationId, copy, endTurn, isCurrent, playReply],
  );

  return {
    state,
    streamingText,
    messages,
    findings,
    error,
    send,
    cancel,
    setListening,
    clearError,
    reset,
    voiceAvailable,
  };
}

/** Guards against a retried or duplicated event writing the same row twice. */
function append(list: Message[], message: Message): Message[] {
  return list.some((m) => m.id === message.id) ? list : [...list, message];
}

/**
 * Swaps the stored row in for the optimistic one, in place, leaving exactly one
 * copy of it. Both ids are checked because the server reuses an unanswered row: a
 * turn that failed after `user_message` and was then retried would otherwise put
 * the same stored id in the list twice, which React renders as two <li>s with the
 * same key.
 */
function reconcile(list: Message[], provisionalId: number, message: Message): Message[] {
  const next: Message[] = [];
  let placed = false;
  for (const item of list) {
    if (item.id !== provisionalId && item.id !== message.id) {
      next.push(item);
      continue;
    }
    if (placed) continue;
    next.push(message);
    placed = true;
  }
  if (!placed) next.push(message);
  return next;
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function describe(err: unknown, copy: Copy): string {
  if (err instanceof ApiError) {
    if (err.isNetwork) return copy.errorNetwork;
    // A `http_<status>` code means the server sent no envelope, so its message is a
    // raw status phrase rather than anything written for a person to read.
    if (!err.code.startsWith("http_")) return err.message;
  }
  return copy.errorGeneric;
}
